/**
 * Popover placement math for the tour overlay.
 *
 * Split out of `TourOverlay.tsx` because this is the part with real edge cases
 * — an anchor in the corner, an anchor taller than the viewport, a popover
 * with nowhere to go — and it's worth testing directly rather than through a
 * rendered component.
 */

import type { TourPlacement } from './types';

export interface Rect { top: number; left: number; width: number; height: number; }

export const POPOVER_WIDTH = 328;
/** Space between the spotlight ring and the popover. */
export const GAP = 16;
/** Breathing room between the spotlight ring and the anchor's own edges. */
export const PAD = 6;
/** Keep the popover at least this far from the window edges. */
export const MARGIN = 12;

/** The spotlight hole: the anchor rect, padded, with top/left clamped inside the viewport. */
export function spotlightHole(rect: Rect): Rect {
  return {
    top: Math.max(0, rect.top - PAD),
    left: Math.max(0, rect.left - PAD),
    width: rect.width + PAD * 2,
    height: rect.height + PAD * 2,
  };
}

/**
 * Choose a side that fits, preferring the step's own request, then the
 * opposite side (which keeps the popover on the intended axis), then whatever
 * fits at all. If nothing fits we return the preference and let the clamp in
 * `popoverPosition` keep it on screen.
 */
export function resolvePlacement(
  preferred: TourPlacement,
  rect: Rect,
  popHeight: number,
  vw: number,
  vh: number,
): TourPlacement {
  const fits: Record<TourPlacement, boolean> = {
    right: rect.left + rect.width + GAP + POPOVER_WIDTH + MARGIN <= vw,
    left: rect.left - GAP - POPOVER_WIDTH - MARGIN >= 0,
    bottom: rect.top + rect.height + GAP + popHeight + MARGIN <= vh,
    top: rect.top - GAP - popHeight - MARGIN >= 0,
  };
  if (fits[preferred]) return preferred;
  const opposite: Record<TourPlacement, TourPlacement> = {
    right: 'left', left: 'right', top: 'bottom', bottom: 'top',
  };
  if (fits[opposite[preferred]]) return opposite[preferred];
  return (['right', 'bottom', 'left', 'top'] as TourPlacement[]).find((p) => fits[p]) ?? preferred;
}

/** Top-left of the popover for a placement, clamped inside the viewport. */
export function popoverPosition(
  placement: TourPlacement,
  rect: Rect,
  popHeight: number,
  vw: number,
  vh: number,
): { top: number; left: number } {
  let top: number;
  let left: number;
  switch (placement) {
    case 'left':
      left = rect.left - GAP - POPOVER_WIDTH;
      top = rect.top + rect.height / 2 - popHeight / 2;
      break;
    case 'top':
      left = rect.left + rect.width / 2 - POPOVER_WIDTH / 2;
      top = rect.top - GAP - popHeight;
      break;
    case 'bottom':
      left = rect.left + rect.width / 2 - POPOVER_WIDTH / 2;
      top = rect.top + rect.height + GAP;
      break;
    case 'right':
    default:
      left = rect.left + rect.width + GAP;
      top = rect.top + rect.height / 2 - popHeight / 2;
      break;
  }
  // Math.max on the upper bound keeps the clamp sane when the viewport is
  // smaller than the popover — better pinned to the top-left than off-screen
  // with a negative bound inverting the clamp.
  return {
    left: Math.min(Math.max(MARGIN, left), Math.max(MARGIN, vw - POPOVER_WIDTH - MARGIN)),
    top: Math.min(Math.max(MARGIN, top), Math.max(MARGIN, vh - popHeight - MARGIN)),
  };
}
